import React, { useRef, useState } from 'react';
import findIndex from 'lodash/findIndex';
import classNames from 'classnames';

import { UsageConditionPopover } from './UsageConditionPopover/UsageConditionPopover';
import { useDelayedHover } from './useDelayedHover';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { targetToIconCode } from '../../utils/utils-target-to-icon-code';
import { LENDING_MODEL_OCOU, LENDING_MODEL_SIM, UNLIMITED_USAGE_LIMIT } from '../../../store/constants';
import { Article, Price } from '../../../../models/Article';
import { ProductMetaData } from '../../../../models/Product';
import { useAvailablePrices } from './useAvailablePrices';
import { ButtonGroup, GroupButton } from '../ButtonGroup';
import { CAMPAIGN_PRODUCTS, useFeature } from '../../utils/togglz';
import { useCampaignProduct } from './useCampaignProduct';
import './AvailablePricesSelector.scss';
import MetadataUtils from '../../utils/utils-metadata';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../utils/formatCurrency';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * Props for <AvailablePricesSelector>.
 */
interface AvailablePricesSelectorProps {
    /**
     * The product on which this price selection is based.
     */
    readonly product: ProductMetaData & Article;

    /**
     * The chosen target (cart or list).
     */
    readonly target: ListActionTarget;

    /**
     * Callback that is called when a price should be added to the cart or a list.
     */
    readonly onAddPrice?: (price: Price) => Promise<void>;

    /**
     * Callback that is called when a price should be selected within the cart or a list.
     */
    readonly onSelectPrice: (price: Price) => Promise<void> | void;

    /**
     * Indicates whether the price buttons should be disabled.
     */
    readonly disabled: boolean;

    /**
     * Indicates whether the selected price should be highlighted.
     */
    readonly showSelectedPrice: boolean;

    /**
     * ePress uses the lengthInDays as term limit only for the view
     */
    readonly customTermLimitForLabel?: number;
}

export function AvailablePricesSelector({
    product,
    target,
    onAddPrice = async () => {},
    // Not used for now, users must add different usage condition from product list / details. May be readded in the
    // future.
    onSelectPrice,
    disabled = false,
    showSelectedPrice = false,
    customTermLimitForLabel = product?.subscriptionPeriod?.lengthInDays
}: AvailablePricesSelectorProps) {
    const [addingToCart, setAddingToCart] = useState(false);
    const [selectingIndex, setSelectingIndex] = useState<number | null>(null);
    const [somePopupVisible, setSomePopupVisible] = useState(false);
    const { t } = useTranslation();
    const { availablePrices } = useAvailablePrices(product);

    const selectedIndex = product.price
        ? showSelectedPrice
            ? findIndex(availablePrices, hasUsageConditionsOf(product.price))
            : undefined
        : undefined;
    const isSomePriceSelected = selectedIndex >= 0;

    const discounted = isDiscounted(product);
    const preorder = MetadataUtils.isProductPreOrderable(product);

    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);

    const { isCampaignProduct, saleEndsSoon, saleEndsSoonLabel } = useCampaignProduct(product);

    const handleAddPrice = async (price: Price) => {
        setAddingToCart(true);
        try {
            await onAddPrice(price);
        } finally {
            setAddingToCart(false);
        }
    };

    const handleSelectPrice = async (price: Price, index: number) => {
        setSelectingIndex(index);
        try {
            await onSelectPrice(price);
        } finally {
            setSelectingIndex(null);
        }
    };

    return (
        <div
            className={classNames('available-prices-selector', discounted && 'discounted')}
            data-numoptions={availablePrices.length}
            data-selectedoption={isSomePriceSelected ? selectedIndex + 1 : undefined}
            data-disabled={disabled || addingToCart}
        >
            <ButtonGroup size="lg">
                {availablePrices.map((price, index) => {
                    const currentPriceSelected = index === selectedIndex;

                    const getButtonState = () => {
                        if (addingToCart) {
                            return 'not-clickable';
                        }

                        // We are in a selection list or the cart
                        if (showSelectedPrice) {
                            if (!currentPriceSelected) {
                                return 'disabled';
                            }

                            return disabled ? 'not-clickable' : 'clickable';
                        }

                        return disabled ? 'disabled' : 'clickable';
                    };

                    return (
                        <PriceButton
                            key={keyOf(price)}
                            price={price}
                            customTermLimitForLabel={customTermLimitForLabel}
                            target={target}
                            currency={product.price?.currency ?? ''}
                            state={getButtonState()}
                            showIcon={!disabled && (currentPriceSelected || !isSomePriceSelected)}
                            onAddPrice={() => handleAddPrice(price)}
                            somePopupVisible={somePopupVisible}
                            onPopupVisibleChange={setSomePopupVisible}
                        />
                    );
                })}
            </ButtonGroup>
            <div className="selected-flags">
                {availablePrices.map((price, index) =>
                    index === selectedIndex ? (
                        <div
                            key={keyOf(price)}
                            className={classNames({
                                hide: selectingIndex !== null && selectingIndex !== index
                            })}
                        >
                            {t('selected')}
                        </div>
                    ) : (
                        <div
                            key={keyOf(price)}
                            className={classNames('select', {
                                loading: selectingIndex === index,
                                hide: selectingIndex !== null && selectingIndex !== index
                            })}
                            onClick={() => handleSelectPrice(price, index)}
                        >
                            {t('select')}
                            {selectingIndex === index ? <LoadingSpinner width={10} className="ml-sm" /> : null}
                        </div>
                    )
                )}
            </div>
            {discounted && (
                <div className="discounted-amount">
                    {t('shop.subscriptions.save', {
                        discount: Math.round((product.price?.savedPercentage ?? 0) / 100)
                    })}
                </div>
            )}
            {preorder && <div className="preorder">{t('shop.subscriptions.preOrder')}</div>}
            {isCampaignsEnabled && isCampaignProduct && saleEndsSoon && (
                <div className="sale-ends-soon">{saleEndsSoonLabel}</div>
            )}
        </div>
    );
}

const POPOVER_HOVER_DELAY = 1000;

/**
 * Props for <PriceButton>.
 */
interface PriceButtonProps {
    /**
     * The price.
     */
    readonly price: Price;

    /**
     * ePress uses the lengthInDays as term limit only for the view
     */
    readonly customTermLimitForLabel?: number;

    /**
     * The chosen target (cart or list).
     */
    readonly target: ListActionTarget;

    /**
     * The active currency.
     */
    readonly currency: string;

    /**
     * Indicates the status of the button. 'clickable' triggers the `onPriceAdd` function, 'not-clickable' and 'disabled' not.
     * 'not-clickable' does not add stylings to the button, 'disabled' adds a "greyed-out" style.
     */
    readonly state: 'clickable' | 'not-clickable' | 'disabled';

    /**
     * Indicates whether the icon should be shown. True by default.
     */
    readonly showIcon?: boolean;

    /**
     * Callback that is called when the button is clicked.
     */
    readonly onAddPrice: () => Promise<void>;

    /**
     * Indicates whether another related popover is already visible.
     */
    readonly somePopupVisible: boolean;

    /**
     * Communicates whether the popover is visible.
     */
    readonly onPopupVisibleChange: (boolean) => void;
}

function PriceButton({
    price,
    customTermLimitForLabel,
    target,
    currency,
    state,
    showIcon = true,
    onAddPrice,
    somePopupVisible,
    onPopupVisibleChange
}: PriceButtonProps) {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const buttonRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [failure, setFailure] = useState(false);
    const { t } = useTranslation();

    const onHover = (hover: boolean) => {
        setPopoverVisible(hover);
        onPopupVisibleChange(hover);
    };

    const {
        onMouseEnter,
        onMouseLeave,
        cancel: cancelPopover
    } = useDelayedHover({
        onHover,
        delayInMs: POPOVER_HOVER_DELAY,
        delayActive: !somePopupVisible
    });

    const { usageLimit, termLimit, lendingModel, netValue } = price;

    const handleClick = () => {
        if (state === 'clickable' && !loading) {
            setLoading(true);
            cancelPopover();
            onAddPrice()
                .then(() => setSuccess(true))
                .catch(() => setFailure(true))
                .finally(() => {
                    setLoading(false);
                    setTimeout(() => {
                        setSuccess(false);
                        setFailure(false);
                    }, 2000);
                });
        }
    };

    const unlimited = lendingModel === LENDING_MODEL_SIM && usageLimit === UNLIMITED_USAGE_LIMIT;

    const labelText = unlimited
        ? t('shop.subscriptions.unlimited')
        : lendingModel === LENDING_MODEL_OCOU
        ? 'OCOU'
        : `${lendingModel}-${usageLimit}`;

    return (
        <>
            <GroupButton
                key={keyOf(price)}
                aria-label={`add product to cart or list_${lendingModel}-${usageLimit}-${
                    customTermLimitForLabel || termLimit
                }`}
                onClick={handleClick}
                ref={buttonRef}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                label={labelText}
                subLabel={t('numberFormat.numberWithCurrencySymbolWithoutThousandsSeparator', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(netValue),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                })}
                icon={showIcon ? targetToIconCode(target) : undefined}
                loading={loading}
                state={state}
                result={success ? 'success' : failure ? 'failure' : undefined}
            />
            <UsageConditionPopover
                target={buttonRef}
                usageCondition={customTermLimitForLabel ? { ...price, termLimit: customTermLimitForLabel } : price}
                visible={popoverVisible}
                price={price}
                currency={currency}
            />
        </>
    );
}

function keyOf({ usageLimit, termLimit, lendingModel }: Price) {
    return `${lendingModel}-${usageLimit}-${termLimit}`;
}

function hasUsageConditionsOf(price: Price) {
    return ({ usageLimit, lendingModel }) => price.usageLimit === usageLimit && price.lendingModel === lendingModel;
}

function isDiscounted(product: Article): boolean {
    return !!product.price && !!product.price?.basePrice;
}
